import { useEffect, useMemo, useState } from 'react';
import usePersistentFilters from 'hooks/usePersistentFilters';
import {
  DEFAULT_SELF_AUTH_FILTERS,
  DEFAULT_STATUS_OPTION,
  DEFAULT_TYPE_OPTION,
} from './selfAuthFilterConstants';

const SELF_AUTH_PERSISTED_FILTERS_KEY = 'selfAuthorizationsListFilters';

const getInitialPersistedFilters = defaultAffiliate => ({
  affiliate: defaultAffiliate,
  ...DEFAULT_SELF_AUTH_FILTERS,
});

const getInitialFilters = defaultAffiliate => ({
  ...getInitialPersistedFilters(defaultAffiliate),
  search: '',
});

const normalizePersistedFilters = (filters, defaultAffiliate = null) => {
  const initialPersistedFilters = getInitialPersistedFilters(defaultAffiliate);

  return {
    affiliate: filters?.affiliate || initialPersistedFilters.affiliate,
    location: Array.isArray(filters?.location) ? filters.location : [],
    product: Array.isArray(filters?.product) ? filters.product : [],
    status: filters?.status || DEFAULT_STATUS_OPTION,
    type: filters?.type || DEFAULT_TYPE_OPTION,
  };
};

const normalizeFilters = (filters, defaultAffiliate = null) => ({
  ...getInitialFilters(defaultAffiliate),
  ...filters,
  location: Array.isArray(filters?.location) ? filters.location : [],
  product: Array.isArray(filters?.product) ? filters.product : [],
  search: filters?.search || '',
  status: filters?.status || DEFAULT_STATUS_OPTION,
  type: filters?.type || DEFAULT_TYPE_OPTION,
});

const areFilterSelectionsEqual = (left, right) => {
  const toValues = selected =>
    [...(selected || [])]
      .map(option => option?.value)
      .filter(Boolean)
      .sort();

  return JSON.stringify(toValues(left)) === JSON.stringify(toValues(right));
};

const useSelfAuthFilters = selectedAffiliate => {
  const initialPersistedFilters = useMemo(
    () => getInitialPersistedFilters(selectedAffiliate),
    [selectedAffiliate]
  );
  const initialFilters = useMemo(() => getInitialFilters(selectedAffiliate), [selectedAffiliate]);

  const [persistedFilters, setPersistedFilters] = usePersistentFilters(
    SELF_AUTH_PERSISTED_FILTERS_KEY,
    initialPersistedFilters
  );

  const [appliedFilters, setAppliedFilters] = useState(
    normalizeFilters(persistedFilters, selectedAffiliate)
  );
  const [draftFilters, setDraftFilters] = useState(
    normalizeFilters(persistedFilters, selectedAffiliate)
  );

  const normalizedInitialFilters = useMemo(
    () => normalizeFilters(initialFilters, selectedAffiliate),
    [initialFilters, selectedAffiliate]
  );

  useEffect(() => {
    if (!selectedAffiliate) {
      return;
    }

    setDraftFilters(previous =>
      previous?.affiliate ? normalizeFilters(previous, selectedAffiliate) : normalizedInitialFilters
    );

    setAppliedFilters(previous =>
      previous?.affiliate ? normalizeFilters(previous, selectedAffiliate) : normalizedInitialFilters
    );

    setPersistedFilters(previous =>
      previous?.affiliate
        ? normalizePersistedFilters(previous, selectedAffiliate)
        : normalizePersistedFilters(normalizedInitialFilters, selectedAffiliate)
    );
  }, [normalizedInitialFilters, selectedAffiliate, setPersistedFilters]);

  const hasChanges = useMemo(() => {
    const hasAffiliateChanges =
      draftFilters?.affiliate?.customerIdName !== appliedFilters?.affiliate?.customerIdName ||
      draftFilters?.affiliate?.dataCompanyId !== appliedFilters?.affiliate?.dataCompanyId;
    const hasLocationChanges = !areFilterSelectionsEqual(
      draftFilters?.location,
      appliedFilters?.location
    );
    const hasProductChanges = !areFilterSelectionsEqual(
      draftFilters?.product,
      appliedFilters?.product
    );
    const hasTypeChanges = draftFilters?.type?.value !== appliedFilters?.type?.value;
    const hasStatusChanges = draftFilters?.status?.value !== appliedFilters?.status?.value;
    const hasSearchChanges = draftFilters?.search !== appliedFilters?.search;

    return (
      hasAffiliateChanges ||
      hasLocationChanges ||
      hasProductChanges ||
      hasTypeChanges ||
      hasStatusChanges ||
      hasSearchChanges
    );
  }, [appliedFilters, draftFilters]);

  const isOnDefaultState = useMemo(() => {
    const isResetState = filters => {
      const normalized = normalizeFilters(filters, selectedAffiliate);

      return (
        !normalized.location.length &&
        !normalized.product.length &&
        !normalized.search &&
        normalized.type?.value === DEFAULT_TYPE_OPTION.value &&
        normalized.status?.value === DEFAULT_STATUS_OPTION.value
      );
    };

    return isResetState(draftFilters) && isResetState(appliedFilters);
  }, [appliedFilters, draftFilters, selectedAffiliate]);

  const handleApply = () => {
    const normalizedDraft = normalizeFilters(draftFilters, selectedAffiliate);

    setAppliedFilters(normalizedDraft);
    setPersistedFilters(normalizePersistedFilters(normalizedDraft, selectedAffiliate));
  };

  const handleReset = () => {
    const affiliate = draftFilters?.affiliate || appliedFilters?.affiliate || selectedAffiliate;
    const resetFilters = normalizeFilters(
      {
        ...getInitialFilters(affiliate || selectedAffiliate),
        affiliate: affiliate || selectedAffiliate,
      },
      selectedAffiliate
    );

    setDraftFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setPersistedFilters(normalizePersistedFilters(resetFilters, selectedAffiliate));
  };

  const setAffiliateFilter = value => {
    const resetState = {
      affiliate: value,
      ...DEFAULT_SELF_AUTH_FILTERS,
    };

    setDraftFilters({ ...resetState, search: '' });
    setAppliedFilters({ ...resetState, search: '' });
    setPersistedFilters(normalizePersistedFilters(resetState, selectedAffiliate));
  };

  const setLocationFilter = value =>
    setDraftFilters(previous => ({ ...previous, location: value }));
  const setProductFilter = value => setDraftFilters(previous => ({ ...previous, product: value }));
  const setStatusFilter = value =>
    setDraftFilters(previous => ({
      ...previous,
      status: value || DEFAULT_STATUS_OPTION,
    }));
  const setTypeFilter = value =>
    setDraftFilters(previous => ({
      ...previous,
      type: value || DEFAULT_TYPE_OPTION,
    }));
  const setSearch = value => setDraftFilters(previous => ({ ...previous, search: value }));

  return {
    appliedFilters,
    draftFilters,
    handleApply,
    handleReset,
    hasChanges,
    isOnDefaultState,
    setAffiliateFilter,
    setLocationFilter,
    setProductFilter,
    setSearch,
    setStatusFilter,
    setTypeFilter,
  };
};

export default useSelfAuthFilters;
